import { usePermissionsForUserLazyQuery } from '@hooks/userPermission';
import { useAuth } from '@context/AuthContext';
import { SalesOrg } from '@graphql/types.generated';
import { useGetUserProfileLazyQuery } from '@hooks/global';
import { getUserData, getUserPermission, storeUserPermission, storeUserData } from '@utils/auth';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { message } from 'antd';
import {
  PERMISSION_PLACE_ORDER,
  PERMISSION_UPDATE_ORDER,
  PERMISSION_VIEW_DEBT_AND_PAYMENT,
  PERMISSION_VIEW_PRICE,
} from '@utils/constants';

const defaultUser = {
  id: '',
  name: '',
  email: '',
  phoneNumber: '',
  qrImage: '',
  customerId: '',
  customerName: '',
  customerEmails: [],
  customerSaleOrg: undefined,
  customerSapId: '',
  customerAddress: '',
  userType: '',
  permissions: [],
  checkPermission: undefined,
  checkPermissionWithEnv: undefined,
};
const env = process.env.NEXT_APP_ENV as 'dev' | 'staging' | 'production';
const ARRAY_PERMISSION_NEED_TO_CHECK_ENV = [PERMISSION_VIEW_PRICE, PERMISSION_VIEW_DEBT_AND_PAYMENT];

function useCurrentUser() {
  const router = useRouter();

  if (router.pathname === '/bypass') return defaultUser;

  const { isLoggedIn } = useAuth();
  const [fetchUserProfile, { data }] = useGetUserProfileLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const user = data.userProfile;
      if (!user) return;
      storeUserData(user);

      fetchUserPermission({
        variables: {
          selectedUserId: data.userProfile?.id || '',
        },
      });
    },
  });

  const [fetchUserPermission] = usePermissionsForUserLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      storeUserPermission(res.permissionsForUser);
    },
  });

  const checkPermission = (permissionName: string) => {
    const permissions = getUserPermission();
    return permissions?.some(e => e.name === permissionName && e.active);
  };

  const checkPermissionWithEnv = (permissionName: string, showMessageError = false) => {
    /*
      TODO: các quyền ở trong mảng ARRAY_PERMISSION_NEED_TO_CHECK_ENV sẽ được bỏ qua, k check quyền ở production do chưa được implement ở BE
    */
    if (env === 'production' && ARRAY_PERMISSION_NEED_TO_CHECK_ENV.includes(permissionName)) {
      return true;
    }
    const permissions = getUserPermission();
    const hasPermission = permissions?.some(e => e.name === permissionName && e.active);
    if (!hasPermission && showMessageError) {
      switch (permissionName) {
        case PERMISSION_UPDATE_ORDER:
          message.error('Bạn không được cấp quyền chỉnh sửa đơn hàng, vui lòng liên hệ quản lí');
          break;
        case PERMISSION_PLACE_ORDER:
          message.error('Bạn không được cấp quyền đặt hàng, vui lòng liên hệ quản lí');
          break;
        case PERMISSION_VIEW_DEBT_AND_PAYMENT:
          message.error(
            'Bạn không được cấp quyền truy cập trang này, vui lòng liên hệ quản lý của bạn để được cấp quyền',
          );
          break;
        default:
          message.error('Bạn chưa được cấp quyền, vui lòng liên hệ quản lí');
          break;
      }
    }
    return hasPermission;
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchUserProfile();
    }
  }, [isLoggedIn]);

  const savedUser = getUserData();
  const name = data?.userProfile?.name || savedUser?.name || '';
  const phoneNumber = data?.userProfile?.phoneNumber || savedUser?.phoneNumber || '';
  const email = data?.userProfile?.email ?? '';
  const id = data?.userProfile?.id || savedUser?.id || '';
  const userType = data?.userProfile?.userType || savedUser?.userType || '';
  const qrImage = data?.userProfile?.customer?.qrImage || savedUser?.customer?.qrImage || '';

  const permissions = getUserPermission();
  return {
    id,
    name,
    email,
    phoneNumber,
    qrImage,
    customerId: (data?.userProfile?.customer?.id as string) || '',
    customerName: (data?.userProfile?.customer?.name as string) || '',
    customerEmails: (data?.userProfile?.customer?.emails as string[]) || [],
    customerSaleOrg: data?.userProfile?.customer?.salesOrg as SalesOrg,
    customerSapId: (data?.userProfile?.customer?.sapId as string) || '',
    customerAddress: (data?.userProfile?.customer?.billingAddress?.body as string) || '',
    userType,
    permissions,
    checkPermission,
    checkPermissionWithEnv,
  };
}

export default useCurrentUser;
